<template>
  <Grid gtr="auto 1fr" class="full-height">
    <slot name="header"></slot>

    <AgGridVue
      floating-filters-height="68"
      class="ag-grid-vue ag-theme-alpine"
      :style="{ '--header-height': headerHeight + 'px' }"
      :row-data="rowData"
      :context="context"
      v-bind="mergedOptions"
      @grid-ready="onGridReady"
      @first-data-rendered="firstDataRendered"
      @rowSelected="rowSelected"
      @cell-value-changed="cellValueChanged"
      @column-visible="onColumnVisible"
      @sort-changed="$emit('sort-changed')"
      @filter-changed="$emit('filter-changed')"
      @cell-clicked="$emit('cell-clicked', $event)"
      @model-updated="$emit('model-updated', $event)"
    />

    <slot name="footer">
      <Grid justify-content="end" class="q-mt-md">
        <div class="text-body-1 text-secondary-1">
          Total rows: {{ computedRowsCount !== null ? computedRowsCount : '...' }}
        </div>
      </Grid>
    </slot>
  </Grid>
</template>

<script>
import { columnTypes } from './column-types'
// Libraries
import { merge, cloneDeep } from 'lodash'
import { AgGridVue } from 'ag-grid-vue'
// Ag grid components
import DropdownFilter from './components/dropdown-filter/DropdownFilter'
import TextOrNumberFilter from './components/text-or-number-filter/TextOrNumberFilter'
import TextDateFilter from './components/text-date-filter/TextDateFilter'
import PriceFilter from './components/price-filter/PriceFilter'
import PriceEditor from './components/price-editor/PriceEditor'
import TextEditor from './components/text-editor/TextEditor'
import DateEditor from './components/date-editor/DateEditor'
import DropdownEditor from './components/dropdown-editor/DropdownEditor'
import CheckboxEditor from './components/checkbox-editor/CheckboxEditor'
import HeaderCheckbox from './components/header-checkbox/HeaderCheckbox'
import LinkRenderer from './components/link-renderer/LinkRenderer'
import StatusesFilter from './components/statuses-filter/StatusesFilter'
import StatusesEditor from './components/statuses-editor/StatusesEditor'
import CustomFilter from './components/custom-filter/CustomFilter'
import ActionsCol from './components/actions-col/ActionsCol'
import FileCol from './components/file-col/FileCol'

export default {
  name: 'AgGrid',

  components: {
    AgGridVue,
    /* eslint-disable vue/no-unused-components */
    DropdownFilter,
    TextOrNumberFilter,
    TextDateFilter,
    PriceFilter,
    PriceEditor,
    TextEditor,
    DateEditor,
    DropdownEditor,
    CheckboxEditor,
    HeaderCheckbox,
    LinkRenderer,
    StatusesFilter,
    StatusesEditor,
    ActionsCol,
    FileCol
    /* eslint-enable vue/no-unused-components */
  },

  props: {
    options: {
      type: Object,
      required: true
    },
    rowData: Array,
    apiRequest: Function,
    headerHeight: {
      type: Number,
      default: 48
    },
    context: Object
  },

  data: () => ({
    rowsCount: null
  }),

  computed: {
    defaultOptions() {
      return {
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: 'agTextColumnFilter',
          type: 'cellLeftAligned',
          floatingFilterComponentParams: {
            suppressFilterButton: true
          },
          width: 110,
          minWidth: 110,
          suppressMenu: true
        },
        frameworkComponents: {
          CustomFilter
        },
        headerHeight: this.headerHeight,
        getRowNodeId: data => data.id,
        singleClickEdit: true,
        stopEditingWhenGridLosesFocus: true,
        suppressContextMenu: true,
        columnTypes
      }
    },

    mergedOptions() {
      const columnSpace = {
        sortable: false,
        filter: false,
        resizable: false,
        minWidth: 16,
        width: 16,
        maxWidth: 16,
        suppressNavigable: true
      }
      const options = cloneDeep(this.options)
      options.columnDefs.unshift({ ...columnSpace, field: 'spacer-1' })
      options.columnDefs.push({ ...columnSpace, field: 'spacer-2', pinned: 'right' })
      return merge(this.defaultOptions, options)
    },

    computedRowsCount() {
      if (this.apiRequest) return this.rowsCount
      return this.rowData ? this.rowData.length : null
    }
  },

  created() {
    this.dataSource = null
    this.rows = []
  },

  methods: {
    getAgGrid() {
      // access from parent
      return { gridApi: this.gridApi, columnApi: this.columnApi }
    },

    getGridOptions() {
      return { api: this.gridApi, columnApi: this.columnApi }
    },

    setExportLoadingState(loadingState) {
      this.$emit('export-loading', loadingState)
    },

    getDataSource() {
      // access from parent
      return this.dataSource
    },

    purgeServerSideCache() {
      // access from parent
      this.gridApi.refreshServerSideStore({ purge: true })
    },

    selectOrDeselectAll(select = true) {
      this.gridApi.forEachNode(node => {
        node.setSelected(select)
      })
    },

    rowSelected({ data: row, node: { selected: isSelected } }) {
      this.$emit('row-selected', { row, isSelected })
    },

    cellValueChanged(event) {
      this.$emit('cell-value-changed', event)
    },

    updateRow(nodeId, newData) {
      const node = this.gridApi.getRowNode(nodeId)
      node.setData(newData)
    },

    updateRows(rows) {
      this.gridApi.applyTransaction({ update: rows })
    },

    onGridReady(params) {
      this.gridApi = params.api
      this.columnApi = params.columnApi
      params.api.sizeColumnsToFit()
      this.$emit('ready', { api: params.api, columnApi: params.columnApi })

      if (this.apiRequest && this.options.rowModelType === 'serverSide') {
        this.dataSource = this.createServerSideDatasource(this.apiRequest)
        params.api.setServerSideDatasource(this.dataSource)
      }
    },

    firstDataRendered() {
      this.gridApi.sizeColumnsToFit()
    },

    onColumnVisible(params) {
      params.api.sizeColumnsToFit()
    },

    createServerSideDatasource(apiRequest) {
      const vm = this
      return {
        getRows(params) {
          apiRequest(params.request)
            .then(response => {
              if (!response) return
              const { data } = response
              vm.tableRowsFetched(data.result, params)
            })
            .catch(() => params.fail())
        }
      }
    },

    tableRowsFetched({ last_row, data }, agGridParams) {
      this.rowsCount = last_row
      this.rows.splice(agGridParams.startRow, data.length, ...data)
      agGridParams.success({ rowData: data, rowCount: last_row })
      this.$emit('new-rows-loaded', { totalRows: last_row, newRows: data })
      if (agGridParams.endRow >= last_row) this.$emit('all-rows-loaded')
      if (!last_row) agGridParams.api.showNoRowsOverlay()
      else agGridParams.api.hideOverlay()

      agGridParams.api.sizeColumnsToFit()
    }
  }
}
</script>

<style lang="scss">
@import '~ag-grid-enterprise/dist/styles/ag-grid.scss';
@import '~ag-grid-enterprise/dist/styles/ag-theme-alpine/sass/ag-theme-alpine-mixin';
@import '~@/styles/fonts';
@import '~@/styles/shadows';

$secondary-1: #55597a; // we can't use var(--r-secondary-1)
$secondary-5: #303974; // we can't use var(--r-secondary-5)
$grey-1: #9e9ca0; // we can't use var(--r-grey-1)
$header-background-color: transparent; // Background colour for all headers, including the grid header, panels etc
$cell-horizontal-padding: 8px; // we can't use var(--r-space-sm)
$row-height: 51px;
$white-2: #f8f9fe; // we can't use var(--r-white-2);
$white-5: #d6d9e2; // we can't use var(--r-white-5);

.ag-theme-alpine {
  --filter-height: 36px;

  @include ag-theme-alpine(
    (
      data-color: $secondary-5,
      foreground-color: $grey-1,
      secondary-foreground-color: $grey-1,
      header-foreground-color: $grey-1,
      header-background-color: $header-background-color,
      cell-horizontal-padding: $cell-horizontal-padding,
      row-height: $row-height,
      row-hover-color: $white-2,
      input-border-color: $white-5,
      input-focus-border-color: $white-5,
      input-focus-box-shadow: none,
      borders: false,
      header-column-resize-handle: true,
      header-column-resize-handle-height: 100%,
      header-column-resize-handle-width: 2px,
      header-column-resize-handle-color: $white-5,
      font-family: (
        'Poppins',
        sans-serif
      ),
      checkbox-checked-color: $secondary-1,
      checkbox-unchecked-color: $white-5
    )
  );

  .ag-tooltip {
    background: var(--r-white-1);
    color: var(--r-secondary-1);
    @extend .text-body-1;
  }

  .ag-header-cell-resize {
    height: 50%;
    top: 35%;
  }

  .ag-header-select-all {
    margin-right: 0 !important;

    margin-top: var(--r-space-sm);
  }

  .ag-body-viewport {
    box-shadow: -10px -2px 6px rgba(71, 71, 71, 0.1);
    border-bottom: 1px solid var(--r-white-5);
  }

  .ag-floating-bottom {
    overflow-y: unset !important;
  }

  /* ========================= STARTS Header styling ========================= */

  .ag-header-row {
    &:after {
      display: none;
    }

    &:first-child {
      @extend .text-overline-1;
      color: var(--r-grey-3);

      .multiple-lines .ag-header-cell-text {
        white-space: normal;
      }

      .ag-header-cell {
        padding-bottom: var(--r-space-sm);
        padding-top: var(--r-space-sm);
      }
    }

    &-floating-filter {
      background: var(--r-white-2);
      border-bottom: 1px solid var(--r-white-4);

      .ag-input-field-input {
        height: var(--filter-height);
      }
    }

    .ag-header-cell.text-center .ag-header-cell-label {
      justify-content: center;
    }

    .ag-header-cell-label {
      align-items: flex-end;
    }
  }

  /* ========================= ENDS Header styling ========================= */

  /* ========================= STARTS cell styling ========================= */

  .ag-row {
    border-bottom: 1px solid var(--r-white-4);

    .ag-cell {
      @extend .text-body-1;
      color: var(--r-secondary-5);
      line-height: $row-height;
      font-variant-numeric: tabular-nums;
      border: 0;

      .ag-checkbox-input-wrapper {
        font-size: 13.5px;
        width: 13.5px;
        height: 13.5px;
        line-height: 14px;
      }

      &.ag-right-aligned-cell input {
        text-align: right;
      }

      &:focus,
      .ag-cell-focus {
        background: var(--r-white-4);
        border: none !important;
      }
    }

    .ag-cell.editable-cell {
      border: none !important;
      box-shadow: none;

      > div {
        border-bottom: 1px solid var(--r-white-4);
      }

      > span {
        padding-bottom: 5px;
        border-bottom: 1px dashed var(--r-secondary-1);
      }

      &:hover {
        background: var(--r-white-4);
      }

      &:active {
        background: var(--r-white-5);
      }
    }

    .ag-cell.status-column {
      > span {
        width: 100%;
        height: 100%;
        display: grid;
        place-items: center;

        > .status-col-badge {
          width: 18px;
          height: 18px;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 50%;
          color: var(--r-white-1);
          @extend .text-button-big;

          &[data-status='accepted'] {
            background: #65b5a5;
          }

          &[data-status='pending'] {
            background: #4f829e;
          }

          &[data-status='cancelled'] {
            background: #dd9787;
          }
        }
      }
    }

    .ag-selection-checkbox {
      margin: 0 auto; // centering checkboxes
    }
  }

  /* ========================= ENDS cell styling ========================= */

  /* ========================= STARTS Table Scrollbar styling ========================= */
  .ag-body-viewport.ag-layout-normal {
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-thumb {
      border: 2px solid rgba(0, 0, 0, 0);
      border-top: none;
      border-bottom: none;
      background-clip: padding-box;
      border-radius: 5px;
      background-color: var(--r-secondary-1);
    }
  }

  /* ========================= ENDS Table Scrollbar styling ========================= */
}

.ag-grid-vue {
  height: 100%;
  width: 99.9%; // hack to get rid of the horizontal scroll
}
</style>
